import axios, { AxiosError, type AxiosInstance } from 'axios';

// Get API base URL from environment
export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://compete-pages-api.vercel.app';

// Custom error class
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true, // Important for CORS with credentials
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
export const tokenManager = {
  get: (): string | null => {
    return localStorage.getItem('authToken');
  },

  set: (token: string): void => {
    localStorage.setItem('authToken', token);
  },

  remove: (): void => {
    localStorage.removeItem('authToken');
  },
};

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add cache-busting for theme requests to ensure fresh data
    if (config.url?.includes('/themes')) {
      // Add timestamp query parameter for cache busting
      const separator = config.url.includes('?') ? '&' : '?';
      config.url = `${config.url}${separator}_t=${Date.now()}`;
    }

    // Debug logging for component-related requests
    if (config.url?.includes('/components')) {
      console.log('🌐 API Request:', {
        method: config.method,
        url: config.url,
        data: config.data,
        hasData: !!config.data,
        dataKeys: config.data ? Object.keys(config.data) : [],
      });
    }

    return config;
  },
  error => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  response => response,
  (error: AxiosError) => {
    const responseData = error.response?.data as any;
    const message =
      responseData?.message || responseData?.error || error.message || 'An error occurred';
    const status = error.response?.status || 500;
    const data = error.response?.data as any;

    if (status === 401) {
      // Only clear token if we're not on certain endpoints that might have temporary issues
      const url = error.config?.url || '';

      // Don't immediately clear token for subscription endpoints - might be a backend issue
      if (!url.includes('/subscriptions/')) {
        // Token expired or invalid, clear local storage
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
      }
    }

    if (status === 403 && data?.error === 'EMAIL_NOT_VERIFIED') {
      if (!window.location.pathname.startsWith('/verify-email')) {
        window.location.replace('/verify-email');
      }
    }

    // Handle upgrade required responses
    if (status === 403 && data?.upgradeRequired) {
      // Dispatch a custom event that components can listen to
      const event = new CustomEvent('upgrade-required', {
        detail: {
          message: data.message,
          feature: data.feature,
          requiredPlan: data.requiredPlan,
        },
      });
      window.dispatchEvent(event);
    }

    throw new ApiError(message, status, error.response?.data);
  }
);

export default apiClient;
